<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { useAuthStore } from '~/auth/stores/auth.store';
import { useFormsStore } from '~/forms/store/forms.store';

const props = defineProps({
  selected_forms: {
    type: Array,
    default: () => [],
  },
  forms_store: { type: Object },
  fields: {
    type: Array,
    default: () => ['Status', 'Assignees', 'Start Date', 'Due Date', 'Category', 'Tags'],
  },
});
const emit = defineEmits(['close', 'refresh', 'save']);
const data = ref(null);
const forms_store = props.forms_store || useFormsStore();
const auth_store = useAuthStore();

const has_permission = computed(() => props.selected_forms.map(form => form?.can_modify).filter(permission => permission).length > 0);

function formatAssignee(form_data) {
  const members_add_array = [];
  const members_remove_array = [];
  const teams_add_array = [];
  const teams_remove_array = [];
  form_data?.assignees_group.assignees?.length && form_data?.assignees_group.assignees.forEach((item) => {
    const members_obj = {};
    const teams_obj = {};
    if (item.member) {
      members_obj.uid = item.uid;
      members_obj.access = 'assignee';
      members_add_array.push(item.uid);
      members_remove_array.push(item.uid);
    }
    else {
      teams_obj.uid = item.uid;
      teams_obj.access = 'assignee';
      teams_add_array.push(item.uid);
      teams_remove_array.push(item.uid);
    }
  });

  if (form_data?.assignees_group?.assignees_option) {
    if (form_data?.assignees_group?.assignees_option === 'Assign')
      return { add: [...members_add_array, ...teams_add_array] };
    else
      return { remove: [...members_remove_array, ...teams_remove_array] };
  }
  else {
    return {};
  }
}

function formatTags(form_data) {
  const tag_obj = form_data?.tags_group;

  if (tag_obj?.tags_option) {
    if (tag_obj.tags_option === 'add') {
      return { add: tag_obj.tags };
    }
    else {
      return { remove: tag_obj.tags };
    }
  }
  else {
    return {};
  }
}

function payloadFormat() {
  const arr = [];
  props.selected_forms.forEach((element) => {
    const obj = {};
    obj.uid = element.uid;
    obj.name = element.name;
    if (data.value.assignees_group)
      obj.assignees = formatAssignee(data.value);
    if (data.value.tags_group)
      obj.tags = formatTags(data.value);
    if (data?.value?.start_date)
      obj.start_date = new Date(data?.value?.start_date).toISOString();
    if (data?.value?.due_date)
      obj.due_date = new Date(data?.value?.due_date).toISOString();
    if (data?.value?.category)
      obj.category = data?.value?.category;
    arr.push(obj);
  });
  return arr;
}

async function onSave() {
  await forms_store.update_forms({ body: { forms: { update: payloadFormat() } } }, 'bulk');
  const count = props.selected_forms.length;
  if (count) {
    const properties = [];
    if (data?.value?.assignees_group?.assignees?.length)
      properties.push('Assignees');
    if (data?.value?.tags_group?.tags?.length)
      properties.push('Tags');
    if (data?.value?.start_date)
      properties.push('Start date');
    if (data?.value?.due_date)
      properties.push('Due date');
    if (data?.value?.category)
      properties.push('Category');
    properties.forEach((property) => {
      forms_store.forms_track_events(`${property} updated`, props.selected_forms[0].uid, { mode: count > 1 ? 'Bulk' : 'Single', count });
    });
  }
  emit('refresh');
  emit('close');
  emit('save');
}
</script>

<template>
  <hawk-modal-container content_class="!w-[650px]">
    <Vueform
      v-model="data"
      :endpoint="onSave"
      size="sm"
      :display-errors="false"
      :presets="['']"
      sync
      :float-placeholders="false" class="grid grid-rows-1 grid-flow-col gap-4" :columns="{
        default: { container: 12 },
        sm: { container: 12, label: 12 },
        md: { container: 12, label: 4 },
      }"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="emit('close')">
          <template #title>
            {{ $t('update') }} {{ selected_forms.length }} {{ $t('forms') }}
          </template>
        </hawk-modal-header>
        <hawk-modal-content>
          <template v-for="field in has_permission ? fields : []">
            <!-- Assignees -->
            <ObjectElement
              v-if="field === 'Assignees'"
              :key="field"
              class="mb-4"
              name="assignees_group"
              :label="$t('Assignees')"
            >
              <RadiogroupElement
                name="assignees_option"
                :items="[
                  { value: 'Assign', label: $t('Assign') },
                  { value: 'Unassign', label: $t('Unassign') },
                ]"
              />
              <hawk-assignee-input

                :add-classes="{
                  ElementLabel: {
                    container: ['hidden'],
                  },
                  ElementLayout: {
                    innerContainer: ['md:!col-span-12'],
                  },
                }"
                class="mb-6"
                :options="{
                  name: 'assignees',
                  has_teams: true,
                  placeholder: $t('select members and teams'),
                  rules: [{
                    required: ['assignees_group.assignees_option', ['Assign', 'Unassign']],

                  },
                  ],
                }"
                :multi="true"
              />
            </ObjectElement>
            <!-- Start Date -->
            <DateTimeElement
              v-if="field === 'Start Date'"
              :key="field"
              :options="{
                format: 'dd/MM/yyyy',
                minDate: new Date(),
                placeholder: $t('Select start date'),
              }"
              class="mb-4" name="start_date"
              :label="$t('Start date')"
            />
            <!-- Due Date -->
            <DateTimeElement
              v-if="field === 'Due Date'"
              :key="field"
              :options="{
                format: 'dd/MM/yyyy',
                minDate: new Date(),
                placeholder: $t('Select due date'),
              }"
              class="mb-4" name="due_date"
              :label="$t('Due date')"
            />
            <!-- Category -->
            <hawk-category-input
              v-if="field === 'Category'"
              :key="field" class="mb-4" :options="{
                name: 'category',
                label: $t('Category'),
                placeholder: $t('Select Category'),
                tags_removable: true,
              }"
            />
            <!-- Tags -->
            <ObjectElement
              v-if="field === 'Tags'"
              :key="field"
              class="mb-4"
              name="tags_group"
              :label="$t('Tags')"
            >
              <RadiogroupElement
                name="tags_option"
                :items="[
                  { value: 'add', label: $t('Add') },
                  { value: 'remove', label: $t('Remove') },
                ]"
              />
              <hawk-tags-input
                class="mb-4"
                :options="{
                  name: 'tags',
                  placeholder: $t('Select tags'),
                  tags_removable: true,
                  rules: [
                    {
                      required: ['tags_group.tags_option', ['add', 'remove']],
                    },
                  ],
                }"
              />
            </ObjectElement>
          </template>
          <FormDetailBulkUpdateCta
            v-if="auth_store.check_split('bulk_fill_forms')"
            :selected_forms="selected_forms"
            :forms_store="forms_store"
            @bulk-submit="emit('close')"
          />
        </hawk-modal-content>
        <hawk-modal-footer>
          <template #right>
            <div class=" flex justify-end text-sm font-medium">
              <hawk-button
                class="mr-3"
                type="outlined"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </hawk-button>
              <ButtonElement
                v-if="has_permission"
                button-class="w-full bg-blue-600" name="submit" :submits="true"
              >
                {{ $t('Save') }}
              </ButtonElement>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
